var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { State, Action, Selector } from '@ngxs/store';
import { WidgetStateModel } from './widget.state.model';
import { ResetWidget, LogWidgetInteraction, LogWidgetInteractionFailure, LogWidgetInteractionSuccess, AuthWidget, AuthWidgetSuccess, AuthWidgetFailure, SetupWidget, SetInputEnabled } from './widget.actions';
import { WidgetAnalyticsService } from 'src/app/widget-auth/widget-analytics.service';
import { WidgetAuthService } from 'src/app/widget-auth/widget-auth.service';
import { finalize } from 'rxjs/operators';
import { UserAnalyticModel } from '../models/user-analytic.model';
import { DeveloperAnalyticModel } from '../models/developer-analytic.model';
import { AnalyticActivityModel } from '../models/analytic-activity.model';
import { WidgetUserTypeEnum } from '../models/widget-user-type.enum';
import { WidgetPlatformEnum } from '../models/widget-platform.enum';
import { WidgetInteractionEnum } from '../models/widget-interaction.enum';
import { plainToClass } from 'class-transformer';
var initialState = {
    // auth state
    userType: WidgetUserTypeEnum.User,
    authenticating: false,
    authenticated: false,
    // widget config
    widgetConfig: {
        id: '',
        type: ''
    },
    // auth data
    parentUrl: {
        full: '',
        domain: ''
    },
    embedded: false,
    // engine params
    engineVersion: '',
    engineAPIVersion: '',
    inputEnabled: true
};
var WidgetState = /** @class */ (function () {
    function WidgetState(authService, analyticsService) {
        this.authService = authService;
        this.analyticsService = analyticsService;
    }
    WidgetState.getUserType = function (state) {
        return state.userType;
    };
    WidgetState.getWidgetAuthenticating = function (state) {
        return state.authenticating;
    };
    WidgetState.getWidgetAuthenticated = function (state) {
        return state.authenticated;
    };
    WidgetState.getInputEnabled = function (state) {
        return state.inputEnabled;
    };
    WidgetState.getEmbedded = function (state) {
        return state.embedded;
    };
    WidgetState.getParentURL = function (state) {
        return state.parentUrl.full;
    };
    WidgetState.prototype.setupWidget = function (context, _a) {
        var hostingURL = _a.hostingURL, embedded = _a.embedded, widgetConfig = _a.widgetConfig;
        // extract host + port (optional) from url, match: 'protocol://host.name:port/path' -> 'host.name:port'
        var match = hostingURL.match(/:\/\/(.[^/]+)/);
        var domain = (match && match[1]) ? match[1] : hostingURL;
        context.patchState({
            // user type
            userType: widgetConfig.uaid ? WidgetUserTypeEnum.User : WidgetUserTypeEnum.Developer,
            // embedded state
            embedded: embedded,
            // config
            widgetConfig: widgetConfig,
            // hosting
            parentUrl: {
                full: hostingURL,
                domain: domain
            }
        });
    };
    WidgetState.prototype.authWidget = function (context, action) {
        var widgetState = context.getState();
        var userType = widgetState.userType, widgetConfig = widgetState.widgetConfig;
        var authRequest$ = userType === WidgetUserTypeEnum.User
            // user auth: via uaid
            ? this.authService.authUser(widgetConfig.uaid)
            // developer auth: via developer key
            : this.authService.authDeveloper(this.buildDeveloperAnalytic(widgetState, WidgetInteractionEnum.Init, WidgetPlatformEnum.WidgetAPI) // build developer auth
            );
        // begin auth
        context.patchState({ authenticating: true });
        // check auth
        authRequest$
            .pipe(finalize(function () { return context.patchState({ authenticating: false }); }))
            .subscribe(function (response) {
            var success = response.success;
            context.patchState({ authenticated: success });
            context.dispatch(success ? new AuthWidgetSuccess(userType, response) : new AuthWidgetFailure(userType, response));
        });
    };
    WidgetState.prototype.logWidgetInteraction = function (context, _a) {
        var interaction = _a.interaction, source = _a.source;
        var widgetState = context.getState();
        var userType = widgetState.userType;
        var analytic = userType === WidgetUserTypeEnum.User
            ? this.buildUserAnalytic(widgetState, interaction, source)
            : this.buildDeveloperAnalytic(widgetState, interaction);
        // log interaction
        this.analyticsService.sendAnalytic(analytic)
            .subscribe(function (response) {
            if (response.success) {
                context.dispatch(new LogWidgetInteractionSuccess(interaction, response));
            }
            else {
                context.dispatch(new LogWidgetInteractionFailure(interaction, response));
            }
        });
    };
    WidgetState.prototype.resetWidget = function (context, action) {
        context.setState(initialState);
    };
    WidgetState.prototype.buildAppActivityData = function (widgetState) {
        var engineVersion = widgetState.engineVersion, engineAPIVersion = widgetState.engineAPIVersion, widgetConfig = widgetState.widgetConfig;
        // require activity data
        var baseActivityData = {
            engine_version: engineVersion,
            api_version: engineAPIVersion
        };
        // include widget params
        Object.entries(widgetConfig)
            // exclude developer key
            .filter(function (_a) {
            var _b = __read(_a, 1), key = _b[0];
            return key !== 'dk';
        })
            // set url param: i.e., ?ui-info=true -> { ... 'url_param_ui-info': 'true' }
            .forEach(function (_a) {
            var _b = __read(_a, 2), key = _b[0], value = _b[1];
            baseActivityData["url_param_" + key] = value;
        });
        return plainToClass(AnalyticActivityModel, baseActivityData);
    };
    WidgetState.prototype.setInputEnabled = function (context, action) {
        context.patchState({
            inputEnabled: action.enabled
        });
    };
    WidgetState.prototype.buildUserAnalytic = function (widgetState, activity, source) {
        var _a;
        var widgetConfig = widgetState.widgetConfig, parentUrl = widgetState.parentUrl;
        var uaid = widgetConfig.uaid;
        var domain = parentUrl.domain;
        var activityData = __assign({}, this.buildAppActivityData(widgetState), (_a = { 
                // include user keys + host
                parent_analytic_identifier: uaid, referer_domain: domain }, _a[activity + "_source"] = source, _a));
        var analytic = {
            analytic_platform: WidgetPlatformEnum.Widget,
            analytic_type: activity,
            analytic_data: activityData
        };
        return plainToClass(UserAnalyticModel, analytic);
    };
    WidgetState.prototype.buildDeveloperAnalytic = function (widgetState, activity, platform) {
        if (platform === void 0) { platform = WidgetPlatformEnum.Widget; }
        var widgetConfig = widgetState.widgetConfig, parentUrl = widgetState.parentUrl, embedded = widgetState.embedded;
        var activityData = this.buildAppActivityData(widgetState);
        var analytic = {
            platform: platform,
            app_activity: activity,
            // app keys
            app_productkey: widgetConfig['app-productkey'],
            app_userid: widgetConfig['app-userid'],
            // user keys
            app_apikey: widgetConfig['dk'],
            app_domain: parentUrl.domain,
            app_activity_data: activityData,
            widget_is_fullscreen: !embedded
        };
        return plainToClass(DeveloperAnalyticModel, analytic);
    };
    WidgetState.prototype.ngxsOnInit = function (context) {
        // set fixed state
        context.patchState({
            engineVersion: Human.VERSION,
            engineAPIVersion: Human.API_VERSION
        });
    };
    __decorate([
        Action(SetupWidget),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SetupWidget]),
        __metadata("design:returntype", void 0)
    ], WidgetState.prototype, "setupWidget", null);
    __decorate([
        Action(AuthWidget),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, AuthWidget]),
        __metadata("design:returntype", void 0)
    ], WidgetState.prototype, "authWidget", null);
    __decorate([
        Action(LogWidgetInteraction),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, LogWidgetInteraction]),
        __metadata("design:returntype", void 0)
    ], WidgetState.prototype, "logWidgetInteraction", null);
    __decorate([
        Action(ResetWidget),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, ResetWidget]),
        __metadata("design:returntype", void 0)
    ], WidgetState.prototype, "resetWidget", null);
    __decorate([
        Action(SetInputEnabled),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object, SetInputEnabled]),
        __metadata("design:returntype", void 0)
    ], WidgetState.prototype, "setInputEnabled", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [WidgetStateModel]),
        __metadata("design:returntype", void 0)
    ], WidgetState, "getUserType", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [WidgetStateModel]),
        __metadata("design:returntype", void 0)
    ], WidgetState, "getWidgetAuthenticating", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [WidgetStateModel]),
        __metadata("design:returntype", void 0)
    ], WidgetState, "getWidgetAuthenticated", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [WidgetStateModel]),
        __metadata("design:returntype", void 0)
    ], WidgetState, "getInputEnabled", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [WidgetStateModel]),
        __metadata("design:returntype", void 0)
    ], WidgetState, "getEmbedded", null);
    __decorate([
        Selector(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [WidgetStateModel]),
        __metadata("design:returntype", void 0)
    ], WidgetState, "getParentURL", null);
    WidgetState = __decorate([
        State({
            name: 'widget',
            defaults: initialState
        }),
        __metadata("design:paramtypes", [WidgetAuthService,
            WidgetAnalyticsService])
    ], WidgetState);
    return WidgetState;
}());
export { WidgetState };
